import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Selectors from 'APP_ROOT/data/settings/selectors';
import { loadSettings, changeSetting } from 'APP_ROOT/data/settings/actions';
import ProgressOverlay from 'APP_ROOT/components/common/ProgressOverlay';
import { strings, getLanguageSubtag, getCurrentLocale } from 'APP_ROOT/localization';
import { ConfirmationDialog } from 'APP_ROOT/components/common/Modal2';

class Language extends React.PureComponent {

    constructor(props) {

        super(props);

        this.state = {
            confirmationDialog: null, // to confirm auto-redirect upon detecting conflict between current locale and user preference
        }

        this.confirmationDialogTempContainer = React.createRef();
        this.confirmationDialogDOMelement = undefined;
        this.confirmationDialogDismissed = false; // allow a dismissed confirmationDialog to remain dismissed during pending state changes
        this.confirmationDialogUserSettingValue = undefined;
    }
    static propTypes = {
        stage: PropTypes.string.isRequired,
        changeLanguage: PropTypes.func.isRequired,
        languageSetting: PropTypes.object,
        hideHeader: PropTypes.bool, // adding this boolean to quickly support cleaner usage of Modal2's integrated header
        onSuccess: PropTypes.func // optional for modal
    }

    componentDidMount() {
        this.props.loadSettings();
        this.checkForCurrentLocaleAndUserSettingConflict();
        /* introduced bugs during edge case tests:
        if (!this.props.settingsLoaded) {
            this.props.loadSettings();
        } else {
            this.checkForCurrentLocaleAndUserSettingConflict();
        }*/
    }

    resolveCurrentLocaleAndUserSettingConflict = () => {

        if (this.confirmationDialogDismissed) {
            console.log("Language resolveCurrentLocaleAndUserSettingConflict is pending");
            return;
        }


        if (this.state.confirmationDialog) {
            console.log("Language resolveCurrentLocaleAndUserSettingConflict is already active.");
            return;
        }
        const value = this.props.languageSetting ? this.props.languageSetting.value : undefined;

        if (!value) {
            console.warn("Language resolveCurrentLocaleAndUserSettingConflict with unknown user language setting");
            return;
        }

        const valueAsLanguageSubtag = getLanguageSubtag(value);

        if (!valueAsLanguageSubtag) {
            console.warn("Language resolveCurrentLocaleAndUserSettingConflict getLanguageSubtag returned undefined");
            return;
        }

        let cancelButtonTitle = strings.locale_name_fr_ca;
        let cancelButtonValue = "fr";
        let submitButtonTitle = strings.locale_name_en_ca;
        let submitButtonValue = "en";
        if (valueAsLanguageSubtag === "fr") {
            cancelButtonTitle = strings.locale_name_en_ca;
            cancelButtonValue = "en";
            submitButtonTitle = strings.locale_name_fr_ca;
            submitButtonValue = "fr";
        }

        this.setState({
            confirmationDialog: <ConfirmationDialog
                title={strings.settings_language_header}
                message={strings.settings_language_description}
                hideHeaderCloseButton={true} // force user to choose
                cancelButtonAction={() => { // alternative language (the one not set in the User API)
                    this.hideAndRemoveConfirmationDialog();
                    this.handleOptionChange({target: {value: cancelButtonValue}});
                }}
                cancelButtonTitle={cancelButtonTitle}
                submitButtonAction={() => {
                    this.hideAndRemoveConfirmationDialog();
                    this.handleOptionChange({target: {value: submitButtonValue}});
                }}
                submitButtonTitle={submitButtonTitle}
            />
        })
    }

    // moves the modal to the parent scope
    unhideAndPresentConfirmationDialog = () => {
        if (!this.confirmationDialogTempContainer || !this.confirmationDialogTempContainer.current) {
            console.error("Language unhideAndPresentConfirmationDialog with invalid temp container reference");
            return;
        }
        this.confirmationDialogDOMelement = this.confirmationDialogTempContainer.current.childNodes[0];
        if (!this.confirmationDialogDOMelement) {
            console.error("Language unhideAndPresentConfirmationDialog confirmationDialogTempContainer is empty.");
            return;
        }
        this.confirmationDialogDOMelement.remove();
        document.body.appendChild(this.confirmationDialogDOMelement);
    }

    // returns the modal to this component scope, then removes
    hideAndRemoveConfirmationDialog = () => {

        if (!this.confirmationDialogTempContainer || !this.confirmationDialogTempContainer.current) {
            console.error("Language hideAndRemoveConfirmationDialog with invalid temp container reference");
            return;
        }
        if (!this.confirmationDialogDOMelement) {
            console.warn("Language hideAndRemoveConfirmationDialog while confirmationDialogDOMelement is already gone.");
            return;
        }
        this.confirmationDialogDismissed = true;

        this.confirmationDialogTempContainer.current.appendChild(this.confirmationDialogDOMelement);
        // it's now safe to tell react to remove it:
        this.setState({confirmationDialog: null});
    }

    checkForCurrentLocaleAndUserSettingConflict = () => {

        // check for conflict between the loaded user setting and the current locale (as per the URL or possibly preset local storage edge case)
        let pending = this.props.languageSetting ? this.props.languageSetting.pending : undefined;
        if (pending) {
            if (this.confirmationDialogDismissed) {
                console.log("Language componentDidUpdate reseting confirmationDialogDismissed");
                this.confirmationDialogDismissed = false;
            }
            return;
        }

        if (this.confirmationDialogUserSettingValue !== undefined) {
            const languageSettingValue = this.props.languageSetting? this.props.languageSetting.value : undefined;
            if (languageSettingValue === this.confirmationDialogUserSettingValue) {
                if (this.props.onSuccess) {
                    this.props.onSuccess();
                }
            }
        }
        
        let valueAsLanguageSubtag = this.props.languageSetting ? getLanguageSubtag(this.props.languageSetting.value) : undefined;  // languageSetting from cloud is "EN_US" or "FR_CA"
        if (!valueAsLanguageSubtag) {
            //console.warn("Language componentDidUpdate not pending but languageSetting value is not defined.");
            return;
        } else {
            const currentLocale = getCurrentLocale();
            if (valueAsLanguageSubtag !== currentLocale) {
                this.resolveCurrentLocaleAndUserSettingConflict();
            }
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (!prevState.confirmationDialog && this.state.confirmationDialog) { // check if this is an update to display the confirmationDialog (thus it's available in the DOM now)
            this.unhideAndPresentConfirmationDialog();
        } else {
            this.checkForCurrentLocaleAndUserSettingConflict();
        }
    }

    handleOptionChange = (event) => {
        const valueAsLanguageSubtag = event.target.value;
        let userSettingValue = "";
        switch (valueAsLanguageSubtag) {
            case "en": userSettingValue = "EN_US"; break;
            case "fr": userSettingValue = "FR_CA"; break;
            default:
                console.error("Language handleOptionChange event has invalid value", event.target.value);
                return;
        }
        this.confirmationDialogUserSettingValue = userSettingValue;

        this.props.changeLanguage(userSettingValue);

        if (this.state.confirmationDialog) {
            this.hideAndRemoveConfirmationDialog();
        }
    };

    render() {

        const value = this.props.languageSetting ? this.props.languageSetting.value : undefined;  // languageSetting from cloud is "EN_US" or "FR_CA"
        const valueAsLanguageSubtag = value !== undefined ? getLanguageSubtag(value) : undefined;
        let pending = this.props.languageSetting ? this.props.languageSetting.pending : undefined;
        if (!valueAsLanguageSubtag) {
            pending = true;
        }

        let modalRadioNameSuffix = this.props.hideHeader? "modal" : "";

        return (
            <div>
                {!this.props.hideHeader ?
                    <h3>{strings.settings_language_header}</h3>
                :
                    null
                }
                <div>{strings.settings_language_description}</div>
                <div className='setting-group-horiz'>
                    <div className='setting-group radio-group'>
                        <div>
                            <input type="radio"
                                name={"language" + modalRadioNameSuffix}
                                id={"language" + modalRadioNameSuffix + ":en"}
                                value='en' // correspond to code used in url ("en/")
                                checked={valueAsLanguageSubtag === 'en'}
                                onChange={this.handleOptionChange}
                                disabled={pending}
                            />
                            <label htmlFor='language:en' disabled={pending}>{strings.locale_name_en_ca}</label>
                        </div>
                        <div>
                            <input type="radio"
                                name={"language" + modalRadioNameSuffix}
                                id={"language" + modalRadioNameSuffix + ":fr"}
                                value='fr'
                                checked={valueAsLanguageSubtag === 'fr'}
                                onChange={this.handleOptionChange}
                                disabled={pending}
                            />
                            <label htmlFor='language:fr' disabled={pending}>{strings.locale_name_fr_ca}</label>
                        </div>
                        { pending && <ProgressOverlay />}
                    </div>
                </div>
                <div style={{position: "absolute",visibility: "hidden"}} ref={this.confirmationDialogTempContainer} >{this.state.confirmationDialog}</div>
            </div>
        );
    }

}

const mapStateToProps = (state, ownProps) => {
    return {
        settingsLoaded: Selectors.settingsLoaded(state),
        languageSetting: Selectors.getSetting(state, 'user.general.locale'),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        loadSettings: () => dispatch(loadSettings(ownProps.stage)),
        changeLanguage: (value) => dispatch(changeSetting(ownProps.stage, 'user.general.locale', value)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Language);